// Shared companion-state renderer (R7).
//
// Every approved review screen renders a Default view with illustrative
// data. The real runtime also needs:
//   - Loading   first paint while fetch resolves
//   - Empty     fetch resolved but nothing to show
//   - Error     fetch rejected or returned 5xx
//   - Degraded  partial data (My Activity aggregator)
//   - Blocked   LIVE_WRITE_GATE OFF or permission denied
//   - Mid-call  rep triggered a write that has not settled yet
//
// This boundary carries no business logic. It renders exactly one state
// using the approved design tokens so the visuals stay inside the
// approved stack. Pass `children` for the Default state.
import type { ReactNode } from 'react';

export type BoundaryState =
  | { kind: 'default' }
  | { kind: 'loading'; label?: string }
  | { kind: 'empty'; title: string; body?: string }
  | { kind: 'error'; title: string; detail?: string; errorCode?: string }
  | {
      kind: 'degraded';
      title: string;
      regions: Array<{ region: string; reason: string }>;
    }
  | { kind: 'blocked'; title: string; reason: string; gateName?: string }
  | { kind: 'midcall'; label: string; actionName?: string };

export interface DataBoundaryProps {
  state: BoundaryState;
  children: ReactNode;
  testId?: string;
}

export function DataBoundary(props: DataBoundaryProps): ReactNode {
  const { state, children, testId } = props;
  if (state.kind === 'default') return <>{children}</>;
  if (state.kind === 'loading') {
    return (
      <section
        role="status"
        aria-live="polite"
        data-testid={testId || 'db-loading'}
        data-boundary-state="loading"
        className="card"
        style={{
          padding: '24px',
          margin: '12px 0',
          background: '#f5f8fc',
          border: '1px dashed #c1cfdd',
          borderRadius: 8,
          color: '#4a6680',
        }}
      >
        <strong style={{ display: 'block', marginBottom: 6 }}>
          {state.label || 'Loading…'}
        </strong>
        <small>Live content will replace this placeholder when the data arrives.</small>
      </section>
    );
  }
  if (state.kind === 'empty') {
    return (
      <section
        role="status"
        data-testid={testId || 'db-empty'}
        data-boundary-state="empty"
        className="card"
        style={{
          padding: '24px',
          margin: '12px 0',
          background: '#fafbfc',
          border: '1px solid #e5e9ef',
          borderRadius: 8,
          color: '#4a6680',
        }}
      >
        <strong style={{ display: 'block', marginBottom: 6 }}>{state.title}</strong>
        {state.body ? <p style={{ margin: 0 }}>{state.body}</p> : null}
      </section>
    );
  }
  if (state.kind === 'error') {
    return (
      <section
        role="alert"
        data-testid={testId || 'db-error'}
        data-boundary-state="error"
        data-error-code={state.errorCode || 'UNKNOWN'}
        className="card"
        style={{
          padding: '24px',
          margin: '12px 0',
          background: '#fbeaea',
          border: '1px solid #e8bdbd',
          borderRadius: 8,
          color: '#8a1f1f',
        }}
      >
        <strong style={{ display: 'block', marginBottom: 6 }}>{state.title}</strong>
        {state.detail ? <p style={{ margin: 0 }}>{state.detail}</p> : null}
        <small style={{ display: 'block', marginTop: 8 }}>
          Error code: <code>{state.errorCode || 'UNKNOWN'}</code>
        </small>
      </section>
    );
  }
  if (state.kind === 'degraded') {
    return (
      <section
        role="status"
        aria-live="polite"
        data-testid={testId || 'db-degraded'}
        data-boundary-state="degraded"
        className="card"
        style={{
          padding: '14px 18px',
          margin: '12px 0',
          background: '#fff5e5',
          border: '1px solid #ffe0b2',
          borderRadius: 8,
          color: '#8a4d00',
        }}
      >
        <strong style={{ display: 'block', marginBottom: 6 }}>{state.title}</strong>
        <ul style={{ margin: '4px 0 0 20px', padding: 0 }}>
          {state.regions.map((r) => (
            <li key={r.region}>
              <code>{r.region}</code>
              <small> ({r.reason})</small>
            </li>
          ))}
        </ul>
      </section>
    );
  }
  if (state.kind === 'blocked') {
    return (
      <section
        role="status"
        data-testid={testId || 'db-blocked'}
        data-boundary-state="blocked"
        data-gate-name={state.gateName}
        className="card"
        style={{
          padding: '24px',
          margin: '12px 0',
          background: '#fff8e1',
          border: '1px solid #ffe082',
          borderRadius: 8,
          color: '#6b5300',
        }}
      >
        <strong style={{ display: 'block', marginBottom: 6 }}>{state.title}</strong>
        <p style={{ margin: 0 }}>{state.reason}</p>
        {state.gateName ? (
          <small style={{ display: 'block', marginTop: 8 }}>
            Gate: <code>{state.gateName}</code>
          </small>
        ) : null}
      </section>
    );
  }
  if (state.kind === 'midcall') {
    return (
      <section
        role="status"
        aria-live="polite"
        aria-busy="true"
        data-testid={testId || 'db-midcall'}
        data-boundary-state="midcall"
        data-action-name={state.actionName}
        className="card"
        style={{
          padding: '14px 18px',
          margin: '12px 0',
          background: '#e8f1fb',
          border: '1px solid #b7ceea',
          borderRadius: 8,
          color: '#1f4b7a',
        }}
      >
        <strong style={{ display: 'block', marginBottom: 6 }}>{state.label}</strong>
        <small>Please wait. This card will update when the provider responds.</small>
      </section>
    );
  }
  return null;
}

/** Classify a client error into a human string. Used by stub ports. */
export function describeErrorCode(code: string | undefined | null): string {
  if (!code) return 'The service could not complete the request.';
  const copy: Record<string, string> = {
    LIVE_WRITE_GATE_OFF: 'This vendor action is paused by its live-write gate.',
    ADAPTER_NOT_IMPLEMENTED: 'This adapter is not wired in the current build.',
    AMBIGUOUS_OUTCOME: 'The provider accepted the request but has not confirmed yet.',
    HUBSPOT_SESSION_INVALID: 'Your HubSpot session needs to be refreshed.',
    HUBSPOT_SESSION_MISSING: 'The HubSpot session handshake has not completed yet.',
    SESSION_EXPIRED: 'Your HubSpot session expired. Reopen the record from HubSpot.',
    READ_ONLY_ROUTE: 'This endpoint is read-only in this build.',
    UNKNOWN: 'An unexpected error occurred.',
  };
  return copy[code] || `Error: ${code}`;
}

export default DataBoundary;
